import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { 
  authenticateScannerCredentials, 
  createScannerSession,
  validateScannerUsername,
  validateScannerPassword 
} from '@/lib/attendance/scanner-auth-utils';
import { toast } from '@/hooks/ui/use-toast';

interface ScannerLoginFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  title?: string;
  description?: string;
}

const ScannerLoginForm: React.FC<ScannerLoginFormProps> = ({
  onSuccess,
  onCancel,
  title = "Scanner Login",
  description = "Enter your scanner credentials to access the pass update system"
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    let isValid = true;
    
    // Validate username
    const usernameValidation = validateScannerUsername(username);
    if (!usernameValidation.valid) {
      setUsernameError(usernameValidation.error || 'Invalid username');
      isValid = false;
    } else {
      setUsernameError(null);
    }
    
    // Validate password
    const passwordValidation = validateScannerPassword(password);
    if (!passwordValidation.valid) {
      setPasswordError(passwordValidation.error || 'Invalid password');
      isValid = false;
    } else {
      setPasswordError(null);
    }
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const authResult = await authenticateScannerCredentials(username, password);
      
      if (authResult.success) {
        // Create session
        createScannerSession(authResult, username);
        
        toast.success(`Logged in as scanner for ${authResult.program_name}`);
        
        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(authResult.error || 'Authentication failed');
        
        toast.error(authResult.error || 'Invalid credentials');
      }
    } catch (err: any) {
      console.error('Scanner login error:', err);
      setError('Login failed. Please try again.');
      
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    // Clear error when user starts typing
    if (usernameError) {
      setUsernameError(null);
    }
    if (error) {
      setError(null);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    // Clear error when user starts typing
    if (passwordError) {
      setPasswordError(null);
    }
    if (error) {
      setError(null);
    }
  };

  return (
    <div className="w-full">
      <Card>
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <ShieldCheck className="h-12 w-12 text-blue-600" />
          </div>
          <CardTitle className="text-xl">{title}</CardTitle>
          {description && (
            <p className="text-sm text-gray-600 mt-2">{description}</p>
          )}
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="scanner-username">Scanner Username</Label>
              <Input
                id="scanner-username"
                type="text"
                placeholder="Enter scanner username"
                value={username}
                onChange={handleUsernameChange}
                disabled={loading}
                className={usernameError ? 'border-red-500' : ''}
                autoComplete="username"
              />
              {usernameError && (
                <p className="text-sm text-red-600">{usernameError}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="scanner-password">Scanner Password</Label>
              <div className="relative">
                <Input
                  id="scanner-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter scanner password"
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={loading}
                  className={passwordError ? 'border-red-500 pr-10' : 'pr-10'}
                  autoComplete="current-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </Button>
              </div>
              {passwordError && (
                <p className="text-sm text-red-600">{passwordError}</p>
              )}
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={loading || !username || !password}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  'Login to Scanner'
                )}
              </Button>
              
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
          
          {/* Help text */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 space-y-1">
              <p>• Scanner credentials are provided by your business owner</p>
              <p>• Scanner access works for all loyalty programs in your business</p>
              <p>• Contact your manager if you need scanner access</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScannerLoginForm;